import { useQueryClient } from "@tanstack/react-query";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  cancelDeliveryCacheUpdates,
  invalidateDeliveryCache,
  patchDeliveryCache,
  restoreDeliveryCache,
  snapshotDeliveryCache,
} from "@/features/delivery/cache";
import {
  useAllowDeliveryMutation,
  useAssignDriverToDeliveryMutation,
} from "@/libs/generated/hooks";
import type {
  AllowDeliveryMutationVariables,
  AssignDriverToDeliveryMutationVariables,
} from "@/libs/generated/operations";

import { getDeliveryPublishPatch, type PublishPricing } from "@/utils/delivery-publish";

import type { Delivery, DeliveryMutationContext } from "../types";

const mutationErrorToast = () => {
  toast.error("Une erreur s'est produite, veuillez réessayer", { position: "bottom-right" });
};

const successToast = () => {
  toast.success("Contrat confirmé avec succès!", { position: "bottom-right" });
};

type UseCreateDeliveryDealMutationsOptions = {
  delivery: Delivery | undefined;
};

export function useCreateDeliveryDealMutations({
  delivery,
}: UseCreateDeliveryDealMutationsOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: allowDelivery } = useAllowDeliveryMutation({
    onMutate: async (variables: AllowDeliveryMutationVariables) => {
      const deliveryId = variables.AdminAllowDeliveryInput.id;
      await cancelDeliveryCacheUpdates(queryClient, deliveryId);
      const snapshot = snapshotDeliveryCache(queryClient, deliveryId);

      if (delivery) {
        patchDeliveryCache(
          queryClient,
          deliveryId,
          getDeliveryPublishPatch(delivery, variables.AdminAllowDeliveryInput)
        );
      }

      return { snapshot };
    },
    onSuccess: async (_result, variables) => {
      await invalidateDeliveryCache(queryClient, variables.AdminAllowDeliveryInput.id);
      successToast();
      void navigate("/admin/pilot/delivery-table/waiting-for-driver");
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      mutationErrorToast();
    },
  });

  const { mutate: assignDriverToDelivery } = useAssignDriverToDeliveryMutation<
    unknown,
    DeliveryMutationContext
  >({
    onMutate: async (variables: AssignDriverToDeliveryMutationVariables) => {
      const deliveryId = variables.UpdateDeliveryDriverInput.id;
      await cancelDeliveryCacheUpdates(queryClient, deliveryId);
      const snapshot = snapshotDeliveryCache(queryClient, deliveryId);

      if (delivery) {
        patchDeliveryCache(
          queryClient,
          deliveryId,
          getDeliveryPublishPatch(delivery, variables.UpdateDeliveryDriverInput)
        );
      }

      return { snapshot };
    },
    onSuccess: async (_result, variables) => {
      await invalidateDeliveryCache(queryClient, variables.UpdateDeliveryDriverInput.id);
      successToast();
      void navigate("/admin/pilot/delivery-table/set");
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      mutationErrorToast();
    },
  });

  return { allowDelivery, assignDriverToDelivery };
}

export function buildAllowDeliveryInput(
  deliveryId: number,
  pricing: PublishPricing
): AllowDeliveryMutationVariables {
  return {
    AdminAllowDeliveryInput: {
      id: deliveryId,
      clientFinalPrice: pricing.clientFinalPrice,
      wantedPrice: pricing.wantedPrice,
      minPrice: pricing.minPrice,
      maxPrice: pricing.maxPrice,
      putInAuctionAt: new Date(),
      status: DeliveryStatus.WAITING_FOR_DRIVER,
      carAvailabilityDate: pricing.carAvailabilityDate,
      carPickUpDate: pricing.carPickUpDate,
      carDeliveryDeadline: pricing.carDeliveryDeadline,
    },
  };
}

export function buildAssignDriverInput(
  deliveryId: number,
  driverId: number,
  pricing: PublishPricing
): AssignDriverToDeliveryMutationVariables {
  return {
    UpdateDeliveryDriverInput: {
      id: deliveryId,
      driverId,
      clientFinalPrice: pricing.clientFinalPrice,
      wantedPrice: pricing.wantedPrice,
      minPrice: pricing.minPrice,
      maxPrice: pricing.maxPrice,
      status: DeliveryStatus.SET,
      carAvailabilityDate: pricing.carAvailabilityDate,
      carPickUpDate: pricing.carPickUpDate,
      carDeliveryDeadline: pricing.carDeliveryDeadline,
    },
  };
}
